import { useState, useEffect, useRef } from "react";

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);

const Frame = ({ title, hint, children }) => (
  <div className="rounded-2xl border border-border bg-card p-4 shadow-xl" data-testid="core-minigame">
    <p className="font-display text-lg font-bold text-primary">{title}</p>
    <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    <div className="mt-3">{children}</div>
  </div>
);

// 1 — Haltering: slip the loop when the ring aligns with the nose (timing).
export const HalterLoopGame = ({ onDone }) => {
  const [pos, setPos] = useState(0);
  const dir = useRef(1);
  const scores = useRef([]);
  const [count, setCount] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => setPos((p) => {
      let n = p + dir.current * 4;
      if (n >= 100) { n = 100; dir.current = -1; }
      if (n <= 0) { n = 0; dir.current = 1; }
      return n;
    }), 30);
    return () => clearInterval(iv);
  }, []);
  const lock = () => {
    if (done) return;
    const s = clamp01(1 - Math.abs(pos - 50) / 30);
    scores.current = [...scores.current, s];
    setCount(scores.current.length);
    if (scores.current.length >= 5) { setDone(true); setTimeout(() => onDone(avg(scores.current)), 300); }
  };
  return (
    <Frame title="Haltering · Slip the Loop" hint="Tap LOCK when the ring lines up over the nose (center green).">
      <div className="relative h-8 rounded-full bg-muted">
        <div className="absolute top-0 h-8 w-[20%] left-[40%] rounded-full bg-green-200" />
        <div className="absolute top-1 h-6 w-6 rounded-full bg-primary" style={{ left: `calc(${pos}% - 12px)` }} />
      </div>
      <button data-testid="halter-lock-btn" disabled={done} onClick={lock}
        className="mt-3 h-11 w-full rounded-xl bg-secondary font-bold text-white active:scale-95">Lock ({count}/5)</button>
    </Frame>
  );
};

// 2 — Leading: keep the pace bar inside the green band (maintenance).
export const LeadFollowGame = ({ onDone }) => {
  const barRef = useRef(50);
  const [bar, setBar] = useState(50);
  const samples = useRef([]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => {
      barRef.current = Math.max(0, barRef.current - 3);
      setBar(barRef.current);
      samples.current.push(barRef.current >= 40 && barRef.current <= 80 ? 1 : 0);
      if (samples.current.length >= 55) { clearInterval(iv); setDone(true); setTimeout(() => onDone(avg(samples.current)), 300); }
    }, 100);
    return () => clearInterval(iv);
  }, []);
  const walk = () => { if (done) return; barRef.current = Math.min(100, barRef.current + 9); setBar(barRef.current); };
  return (
    <Frame title="Leading · Keep Pace" hint="Tap WALK ON to hold the pace bar inside the green band as it drifts down.">
      <div className="relative h-28 w-full overflow-hidden rounded-xl bg-muted">
        <div className="absolute left-0 w-full bg-green-200" style={{ bottom: "40%", height: "40%" }} />
        <div className="absolute left-0 w-full bg-primary transition-all" style={{ bottom: 0, height: `${bar}%` }} />
      </div>
      <button data-testid="lead-walk-btn" disabled={done} onClick={walk}
        className="mt-3 h-11 w-full rounded-xl bg-secondary font-bold text-white active:scale-95">Walk On</button>
    </Frame>
  );
};

// 3 — Feet: tap the highlighted hoof in sequence (spatial reaction).
export const PickFeetGame = ({ onDone }) => {
  const feet = ["Front Left", "Front Right", "Hind Left", "Hind Right"];
  const [seq] = useState(() => Array.from({ length: 6 }, () => Math.floor(Math.random() * 4)));
  const [i, setI] = useState(0);
  const scores = useRef([]);
  const [done, setDone] = useState(false);
  const active = seq[i];
  const tap = (idx) => {
    if (done) return;
    scores.current.push(idx === active ? 1 : 0);
    const ni = i + 1;
    if (ni >= seq.length) { setDone(true); setTimeout(() => onDone(avg(scores.current)), 300); }
    else setI(ni);
  };
  return (
    <Frame title="Picking Feet · Tap the Hoof" hint="Tap the highlighted hoof each time — six in a row.">
      <p className="text-center text-sm font-bold text-secondary" data-testid="feet-prompt">Pick up: {feet[active]}</p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {feet.map((f, idx) => (
          <button key={idx} data-testid={`foot-${idx}`} disabled={done} onClick={() => tap(idx)}
            className={`h-16 rounded-xl border-2 text-sm font-bold active:scale-95 ${idx === active ? "border-secondary bg-secondary/15 text-secondary" : "border-border bg-card text-primary"}`}>{f}</button>
        ))}
      </div>
      <p className="mt-2 text-center text-xs text-muted-foreground">{i}/{seq.length}</p>
    </Frame>
  );
};

// 4 — Tying: react only when the horse pulls (go / no-go inhibition).
export const StayTiedGame = ({ onDone }) => {
  const [pulling, setPulling] = useState(false);
  const pullingRef = useRef(false);
  const handled = useRef(false);
  const hits = useRef(0);
  const pulls = useRef(0);
  const bad = useRef(0);
  const [round, setRound] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    let r = 0;
    const iv = setInterval(() => {
      r++; setRound(r);
      if (r > 10) {
        clearInterval(iv); setDone(true); setPulling(false);
        const score = clamp01((hits.current - bad.current * 0.5) / Math.max(1, pulls.current));
        setTimeout(() => onDone(score), 300);
        return;
      }
      const willPull = Math.random() < 0.5;
      pullingRef.current = willPull; handled.current = false; setPulling(willPull);
      if (willPull) pulls.current++;
    }, 900);
    return () => clearInterval(iv);
  }, []);
  const ease = () => {
    if (done) return;
    if (pullingRef.current && !handled.current) { hits.current++; handled.current = true; pullingRef.current = false; setPulling(false); }
    else bad.current++;
  };
  return (
    <Frame title="Tying · Stay Patient" hint="Tap EASE OFF only when your horse pulls back. Don't fuss when it's calm.">
      <div data-testid="tie-state" className={`grid h-24 place-items-center rounded-xl text-lg font-bold ${pulling ? "bg-red-100 text-red-700" : "bg-green-50 text-green-700"}`}>
        {pulling ? "PULL! Ease off!" : "Calm — wait…"}
      </div>
      <button data-testid="tie-ease-btn" disabled={done} onClick={ease}
        className="mt-3 h-11 w-full rounded-xl bg-secondary font-bold text-white active:scale-95">Ease Off ({round}/10)</button>
    </Frame>
  );
};

// 5 — Trailer: advance up the ramp only when the light is green (gated progress).
export const LoadTrailerGame = ({ onDone }) => {
  const progRef = useRef(0);
  const [prog, setProg] = useState(0);
  const readyRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);
  const finishedRef = useRef(false);
  useEffect(() => {
    function finish(score) { if (finishedRef.current) return; finishedRef.current = true; setDone(true); setTimeout(() => onDone(score), 300); }
    const blink = setInterval(() => { readyRef.current = !readyRef.current; setReady(readyRef.current); }, 700);
    let ticks = 0;
    const timer = setInterval(() => {
      ticks++;
      if (progRef.current >= 100) { clearInterval(blink); clearInterval(timer); finish(1); }
      else if (ticks >= 150) { clearInterval(blink); clearInterval(timer); finish(clamp01(progRef.current / 100)); }
    }, 100);
    return () => { clearInterval(blink); clearInterval(timer); };
  }, []);
  const step = () => {
    if (done) return;
    if (readyRef.current) progRef.current = Math.min(100, progRef.current + 12);
    else progRef.current = Math.max(0, progRef.current - 6);
    setProg(progRef.current);
  };
  return (
    <Frame title="Trailer Loading · Step Up" hint="Tap STEP UP only when the ramp light is GREEN (your horse is willing).">
      <div data-testid="trailer-ready" className={`grid h-12 place-items-center rounded-xl text-sm font-bold ${ready ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
        {ready ? "GREEN — willing" : "wait…"}
      </div>
      <div className="mt-2 h-4 overflow-hidden rounded-full bg-muted"><div className="h-4 bg-primary transition-all" style={{ width: `${prog}%` }} /></div>
      <button data-testid="trailer-step-btn" disabled={done} onClick={step}
        className="mt-3 h-11 w-full rounded-xl bg-secondary font-bold text-white active:scale-95">Step Up ({Math.round(prog)}%)</button>
    </Frame>
  );
};

// 6 — Desensitizing: reassure each scary object before the spook meter fills.
export const DesensitizeGame = ({ onDone }) => {
  const [stim, setStim] = useState(null);
  const stimRef = useRef(null);
  const spook = useRef(0);
  const [spookD, setSpookD] = useState(0);
  const samples = useRef([]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const items = ["🏳️ Flag", "🟦 Tarp", "☂️ Umbrella", "🎈 Balloon"];
    const spawn = setInterval(() => { const it = items[Math.floor(Math.random() * items.length)]; stimRef.current = it; setStim(it); }, 1200);
    const tick = setInterval(() => {
      if (stimRef.current) spook.current = Math.min(100, spook.current + 8);
      else spook.current = Math.max(0, spook.current - 6);
      setSpookD(spook.current);
      samples.current.push(1 - spook.current / 100);
    }, 150);
    const timer = setTimeout(() => { clearInterval(spawn); clearInterval(tick); setDone(true); setTimeout(() => onDone(clamp01(avg(samples.current))), 300); }, 9000);
    return () => { clearInterval(spawn); clearInterval(tick); clearTimeout(timer); };
  }, []);
  const reassure = () => { if (done || !stimRef.current) return; stimRef.current = null; setStim(null); spook.current = Math.max(0, spook.current - 15); setSpookD(spook.current); };
  return (
    <Frame title="Desensitizing · Stay Calm" hint="Tap REASSURE whenever a scary object appears, before the red spook meter fills.">
      <div data-testid="desense-stim" className="grid h-16 place-items-center rounded-xl bg-muted text-lg font-bold text-primary">{stim || "…all quiet…"}</div>
      <div className="mt-2 h-3 overflow-hidden rounded-full bg-muted"><div className="h-3 bg-red-400 transition-all" style={{ width: `${spookD}%` }} /></div>
      <button data-testid="desense-reassure-btn" disabled={done} onClick={reassure}
        className="mt-3 h-11 w-full rounded-xl bg-secondary font-bold text-white active:scale-95">Reassure</button>
    </Frame>
  );
};

// 7 — Saddling: stop the needle in the green center across 3 steps (multi-stage).
export const SaddleUpGame = ({ onDone }) => {
  const steps = ["Saddle Pad", "Saddle", "Cinch"];
  const [step, setStep] = useState(0);
  const [pos, setPos] = useState(0);
  const dir = useRef(1);
  const scores = useRef([]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => setPos((p) => {
      let n = p + dir.current * 5;
      if (n >= 100) { n = 100; dir.current = -1; }
      if (n <= 0) { n = 0; dir.current = 1; }
      return n;
    }), 30);
    return () => clearInterval(iv);
  }, []);
  const stop = () => {
    if (done) return;
    scores.current = [...scores.current, clamp01(1 - Math.abs(pos - 50) / 35)];
    if (step + 1 >= steps.length) { setDone(true); setTimeout(() => onDone(avg(scores.current)), 300); }
    else setStep(step + 1);
  };
  return (
    <Frame title="Saddling · Easy Does It" hint="Tap STEADY when the needle sits in the green center. Three gentle steps.">
      <p data-testid="saddle-step" className="text-center text-sm font-bold text-secondary">Step {step + 1}/3 · {steps[step]}</p>
      <div className="relative mt-2 h-8 rounded-full bg-muted">
        <div className="absolute top-0 h-8 w-[24%] left-[38%] rounded-full bg-green-200" />
        <div className="absolute top-1 h-6 w-2 rounded bg-primary" style={{ left: `calc(${pos}% - 4px)` }} />
      </div>
      <button data-testid="saddle-steady-btn" disabled={done} onClick={stop}
        className="mt-3 h-11 w-full rounded-xl bg-secondary font-bold text-white active:scale-95">Steady</button>
    </Frame>
  );
};

// 8 — First Ride: keep the rider centered by countering the sway (balance).
export const FirstRideBalanceGame = ({ onDone }) => {
  const bal = useRef(50);
  const [balD, setBalD] = useState(50);
  const drift = useRef(1);
  const samples = useRef([]);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => {
      if (Math.random() < 0.15) drift.current *= -1;
      bal.current = Math.max(0, Math.min(100, bal.current + drift.current * 2));
      setBalD(bal.current);
      samples.current.push(1 - Math.abs(bal.current - 50) / 50);
      if (samples.current.length >= 65) { clearInterval(iv); setDone(true); setTimeout(() => onDone(clamp01(avg(samples.current))), 300); }
    }, 100);
    return () => clearInterval(iv);
  }, []);
  const nudge = (d) => { if (done) return; bal.current = Math.max(0, Math.min(100, bal.current + d * 10)); setBalD(bal.current); };
  return (
    <Frame title="First Ride · Find Balance" hint="Keep the rider over the center line. Tap ◀ / ▶ to counter the sway.">
      <div className="relative h-8 rounded-full bg-muted">
        <div className="absolute top-0 left-1/2 h-8 w-1 bg-green-500" />
        <div className="absolute top-1 h-6 w-6 rounded-full bg-primary" style={{ left: `calc(${balD}% - 12px)` }} />
      </div>
      <div className="mt-3 flex gap-2">
        <button data-testid="balance-left-btn" disabled={done} onClick={() => nudge(-1)} className="h-11 flex-1 rounded-xl bg-secondary font-bold text-white active:scale-95">◀ Left</button>
        <button data-testid="balance-right-btn" disabled={done} onClick={() => nudge(1)} className="h-11 flex-1 rounded-xl bg-secondary font-bold text-white active:scale-95">Right ▶</button>
      </div>
    </Frame>
  );
};

// 9 — Walk/Trot/Canter: tap the gait the judge calls, fast (labeled reaction).
export const GaitChangeGame = ({ onDone }) => {
  const gaits = ["Walk", "Trot", "Canter"];
  const [prompt, setPrompt] = useState(null);
  const promptRef = useRef(null);
  const answered = useRef(false);
  const scores = useRef([]);
  const [round, setRound] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    let r = 0;
    const iv = setInterval(() => {
      if (promptRef.current !== null && !answered.current) scores.current.push(0);
      r++; setRound(r);
      if (r > 8) { clearInterval(iv); setDone(true); setPrompt(null); setTimeout(() => onDone(avg(scores.current)), 300); return; }
      const g = Math.floor(Math.random() * 3);
      promptRef.current = g; answered.current = false; setPrompt(g);
    }, 1100);
    return () => clearInterval(iv);
  }, []);
  const tap = (idx) => {
    if (done || promptRef.current === null || answered.current) return;
    answered.current = true;
    scores.current.push(idx === promptRef.current ? 1 : 0);
  };
  return (
    <Frame title="Walk / Trot / Canter · Cue the Gait" hint="Tap the gait the judge calls, as fast as you can!">
      <div data-testid="gait-prompt" className="grid h-16 place-items-center rounded-xl bg-primary/10 text-2xl font-extrabold text-primary">{prompt !== null ? gaits[prompt] : "Ready…"}</div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {gaits.map((g) => (
          <button key={g} data-testid={`gait-${g.toLowerCase()}`} disabled={done} onClick={() => tap(gaits.indexOf(g))}
            className="h-12 rounded-xl bg-secondary font-bold text-white active:scale-95">{g}</button>
        ))}
      </div>
      <p className="mt-2 text-center text-xs text-muted-foreground">{round}/8</p>
    </Frame>
  );
};

export const CORE_GAME_FOR = {
  haltering: HalterLoopGame,
  leading: LeadFollowGame,
  feet: PickFeetGame,
  tying: StayTiedGame,
  trailer: LoadTrailerGame,
  desensitize: DesensitizeGame,
  saddling: SaddleUpGame,
  first_ride: FirstRideBalanceGame,
  wtc: GaitChangeGame,
};
